// Claude API client abstraction
// Routes through Firebase Cloud Function proxy in production.
// Falls back to direct API call in dev if VITE_CLAUDE_API_KEY is set.

import { auth } from './firebase';
import { getAppEnv } from '../config/env';

interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ClaudeRequest {
  model: string;
  max_tokens: number;
  system: string;
  messages: ClaudeMessage[];
}

interface ClaudeResponse {
  id: string;
  content: Array<{ type: 'text'; text: string }>;
  model: string;
  stop_reason: string;
  usage: { input_tokens: number; output_tokens: number };
}

const SONNET_MODEL = 'claude-sonnet-4-6';
const HAIKU_MODEL = 'claude-haiku-4-5-20251001';

export async function callClaude(
  systemPrompt: string,
  messages: ClaudeMessage[],
  options: { model?: 'sonnet' | 'haiku'; maxTokens?: number } = {},
): Promise<string> {
  const model = options.model === 'haiku' ? HAIKU_MODEL : SONNET_MODEL;
  const maxTokens = options.maxTokens ?? 4096;

  const { claudeProxyUrl: proxyUrl, claudeApiKey: apiKey } = getAppEnv();

  const body: ClaudeRequest = {
    model,
    max_tokens: maxTokens,
    system: systemPrompt,
    messages,
  };

  let response: Response;

  if (proxyUrl) {
    // Production: route through Firebase Cloud Function with auth token
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to call Claude API');
    }
    const idToken = await currentUser.getIdToken();

    response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
  } else if (apiKey) {
    // Dev fallback: direct API call (API key exposed in browser — dev only!)
    response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': '2023-06-01',
        'x-api-key': apiKey,
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify(body),
    });
  } else {
    throw new Error('No Claude API configuration found. Set VITE_CLAUDE_API_PROXY_URL (production) or VITE_CLAUDE_API_KEY (dev).');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Claude API error (${response.status}): ${errorText}`);
  }

  const data: ClaudeResponse = await response.json();

  if (!data.content || data.content.length === 0) {
    throw new Error('Claude API returned empty response');
  }

  return data.content[0].text;
}

export { SONNET_MODEL, HAIKU_MODEL };
export type { ClaudeMessage };
